import type { RequestFn } from '../http';
import type { CustomerContractModel } from './weclapp';
import type { PortalAreaValue } from '../../portalAccessTypes';

/** Wo ein Kunde gegenüber einer Leistung steht. `null` = noch nie bewertet: die Lücke. */
export type CustomerOfferingStatus = 'ACTIVE' | 'PROPOSED' | 'DECLINED';

export const OFFERING_STATUS_LABELS: Record<CustomerOfferingStatus, string> = {
  ACTIVE: 'Läuft bereits',
  PROPOSED: 'Anbieten',
  DECLINED: 'Passt nicht',
};

export interface OfferingMatch {
  key: string;
  name: string;
  category: string;
  pitch: string;
  trigger: string;
  effortHint: string;
  valueHint: string;
  customerVisible?: boolean;
  status: CustomerOfferingStatus | null;
  note: string | null;
  since: string | null;
  setByName: string | null;
  updatedAt: string | null;
}

export interface CustomerPortfolioMatch {
  offerings: OfferingMatch[];
  summary: {
    total: number;
    active: number;
    proposed: number;
    declined: number;
    /** Noch nie bewertet — der Grund, warum es diesen Abgleich gibt. */
    unrated: number;
  };
  categoryLabels: Record<string, string>;
  categoryOrder: string[];
}

/**
 * Kundenakte-Pflege im Flyout: Besonderheiten + lokale Ansprechpartner.
 * Kein Portal-Zugang hier — der bleibt bewusst im Admin.
 */

export interface CarePhone {
  /** Bezeichnung, z. B. "Mobil", "Zentrale", "Durchwahl". */
  label: string | null;
  number: string;
}

export interface CareContact {
  id: string;
  name: string;
  email: string | null;
  /** Primärnummer (Spiegel von phones[0]) — fürs Anrufer-Matching. */
  phone: string | null;
  /** Alle Rufnummern des Kontakts. */
  phones: CarePhone[];
  /** Berufsbezeichnung/Position (nicht Portal-Rolle). */
  jobRole: string | null;
  /** Geburtstag als YYYY-MM-DD. */
  birthday: string | null;
  /** Freitext-Hinweise (Erreichbarkeit, Zuständigkeiten, …). */
  notes: string | null;
  role: 'ADMIN' | 'MITARBEITER' | 'GAST';
  isActive: boolean;
  portalEnabled: boolean;
  /**
   * Haupt-Ansprechpartner. Quelle ist die interne Zuständigkeit (PRIMARY);
   * nur ohne interne Pflege gilt Weclapps party.primaryContactId als Fallback.
   */
  isPrimary?: boolean;
  /** Portal-interne Zuständigkeiten (Haupt-AP + Themen wie Angebote/Rechnungen). */
  responsibilities?: ContactResponsibility[];
  /** Zuletzt im CRM-Sync gesehen (ISO) — null, wenn noch nie mitgelaufen. */
  lastSeenInCrmAt?: string | null;
  /**
   * Was das CRM abweichend meldet. Der Sync überschreibt gepflegte Werte nicht
   * mehr — hier steht, worüber der Mensch entscheiden muss.
   */
  crmDivergence?: CrmDivergence;
}

/** Was in der Pauschale steckt bzw. gesondert berechnet wird — Katalogeintrag. */
export interface FairUseScopeItem {
  key: string;
  label: string;
  detail: string;
}

/**
 * Die Toleranzleiter: Je länger der betrachtete Zeitraum, desto enger die Grenze.
 * Ein einzelner Monat darf ausschlagen, eine Serie weniger, das Jahresmittel kaum.
 */
export interface FairUseTolerance {
  singlePercent: number;
  consecutivePercent: number;
  consecutiveCount: number;
  rollingYearPercent: number;
}

/** Ab `fromPercent` Jahresdurchschnitt steigt das Kontingent um `raisePercent`. */
export interface FairUseAdjustmentStep {
  fromPercent: number;
  raisePercent: number;
}

export interface FairUseAdjustment {
  triggerPercent: number;
  steps: FairUseAdjustmentStep[];
  effective: 'NEXT_YEAR' | 'NEXT_PERIOD';
}

/**
 * Die Rechnung hinter der Stundenzahl. Kaufmännische Angabe — nur ADMIN und TEAMLEAD
 * bekommen die Beträge vom Server; für alle anderen kommen sie als null zurück.
 */
export interface FairUseCalculation {
  flatRateMonthly: number | null;
  licenseCostMonthly: number | null;
  internalCostPerHour: number | null;
  targetMarginPercent: number;
  /**
   * Woher Pauschale und Lizenzkosten stammen. Ein späterer Abgleich aus dem Control Hub
   * füllt nur, was nicht auf MANUAL steht — eine bewusst gesetzte Sonderkondition
   * überschreibt er nicht.
   */
  source: 'MANUAL' | 'CONTROL_HUB';
  syncedAt: string | null;
}

/** Ein Zeitfenster an einem Tag, lokale Wanduhr („08:00"–„17:00"). */
export interface DayInterval {
  from: string;
  to: string;
}

/**
 * Die Servicezeit — daran misst sich die SLA-Uhr, sie ist keine Beschreibung mehr.
 * `days` hat sieben Einträge, Index 0 = Montag. Leer = an dem Tag geschlossen.
 */
export interface ServiceWindow {
  timeZone: string;
  days: DayInterval[][];
  /** Bundesland-Kürzel, `DE` = nur bundesweite Feiertage, `NONE` = Feiertage ignorieren. */
  holidayRegion: string;
}

export interface FairUsePolicy {
  basis: 'FLAT' | 'PER_SEAT';
  seats: number | null;
  hoursPerSeat: number | null;
  tolerance: FairUseTolerance;
  adjustment: FairUseAdjustment;
  calculation: FairUseCalculation;
  carryOver: boolean;
  carryOverMaxPercent: number;
  serviceWindow: ServiceWindow;
  includedKeys: string[];
  excludedKeys: string[];
  overrunHandling: string;
  note: string | null;
}

export interface FairUseCatalog {
  /** Auswahl für die Feiertagsregion (Bundesländer + bundesweit + ignorieren). */
  holidayRegions: { value: string; label: string }[];
  /** Hausstandard — gilt, solange für den Kunden nichts Eigenes festgeschrieben ist. */
  house: FairUsePolicy;
  canSeeCalculation: boolean;
  included: FairUseScopeItem[];
  excluded: FairUseScopeItem[];
}
/** Feld-Abweichung zwischen gepflegtem Portal-Wert und CRM. */
export interface CrmDivergence {
  name?: { crm: string; seenAt: string };
  email?: { crm: string; seenAt: string };
  phone?: { crm: string; seenAt: string };
}

/** Anzeigenamen der abweichenden Felder. */
export const DIVERGENCE_FIELD_LABELS: Record<keyof CrmDivergence, string> = {
  name: 'Name',
  email: 'E-Mail',
  phone: 'Telefon',
};

/** Portal-interne Zuständigkeiten eines Ansprechpartners (ServiceHub-eigen). */
export type ContactResponsibility =
  | 'PRIMARY'
  | 'TECHNICAL'
  | 'QUOTES'
  | 'INVOICING'
  | 'ORDERS'
  | 'CONTRACTS'
  | 'MANAGEMENT'
  | 'EMERGENCY';

/** Anzeigenamen — Badges, Auswahl-Liste, Tooltips. Reihenfolge = Anzeigereihenfolge. */
export const RESPONSIBILITY_LABELS: Record<ContactResponsibility, string> = {
  PRIMARY: 'Haupt-AP',
  TECHNICAL: 'Technik / IT',
  QUOTES: 'Angebote',
  INVOICING: 'Rechnungen',
  ORDERS: 'Bestellungen',
  CONTRACTS: 'Verträge & Lizenzen',
  MANAGEMENT: 'Geschäftsführung',
  EMERGENCY: 'Notfall / Eskalation',
};

export const RESPONSIBILITY_ORDER: ContactResponsibility[] = [
  'PRIMARY',
  'TECHNICAL',
  'QUOTES',
  'INVOICING',
  'ORDERS',
  'CONTRACTS',
  'MANAGEMENT',
  'EMERGENCY',
];

/** Öffnungszeiten des Kunden — nicht die Servicezeit (die treibt die SLA-Uhr). */
export interface OpeningHours {
  timeZone: string;
  /** Index 0 = Montag … 6 = Sonntag. Leer = geschlossen. */
  days: DayInterval[][];
  holidayRegion: string;
  /** Betriebsurlaub, ganze Tage, beide Grenzen inklusive (JJJJ-MM-TT). */
  closures: { from: string; to: string; label: string }[];
  note: string;
}

export type OpeningState = 'UNKNOWN' | 'OPEN' | 'BREAK' | 'CLOSED' | 'HOLIDAY' | 'VACATION';

export interface OpeningStatus {
  state: OpeningState;
  until: string | null;
  nextOpen: string | null;
  closureLabel: string | null;
  closureUntil: string | null;
  today: string[];
  summary: string;
  note: string | null;
  timeZone: string;
  computedAt: string;
}

export interface OpeningHoursResearchResult {
  /** false = für KIAN ist keine Websuche eingerichtet. */
  available: boolean;
  found: boolean;
  proposal: OpeningHours | null;
  sources: string[];
  confidence: 'high' | 'medium' | 'low' | null;
  remark: string;
}

export interface CustomerCareData {
  customer: {
    id: string;
    name: string;
    serviceNotes: string | null;
    openingHours: OpeningHours | null;
    openingStatus: OpeningStatus;
    /** In der Pauschale eingeplantes Betreuungskontingent; null = keins vereinbart. */
    includedHoursPerMonth: number | null;
    /** Wie der Kunde für Servicezeit zahlt — Grundlage für den Hinweis im Ticket-Cockpit. */
    contractModel: CustomerContractModel;
    /** Die geltende Vereinbarung — eigene, sonst Hausstandard. */
    fairUse: FairUsePolicy;
    /** false = Hausstandard, für diesen Kunden ist nichts festgeschrieben. */
    fairUseIsOwn: boolean;
    fairUseSummary: string;
    /** false = die Beträge hinter der Stundenzahl wurden nicht mitgeliefert. */
    fairUseCalculationVisible: boolean;
  };
  contacts: CareContact[];
}

export interface CareContactInput {
  name?: string;
  email?: string;
  phone?: string;
  /** Alle Rufnummern; überschreibt serverseitig auch `phone` (Spiegel der ersten Nummer). */
  phones?: { label?: string; number: string }[];
  jobRole?: string;
  /** YYYY-MM-DD; leerer String entfernt den Geburtstag. */
  birthday?: string;
  notes?: string;
  role?: 'ADMIN' | 'MITARBEITER' | 'GAST';
  /** Zuständigkeiten; PRIMARY hält der Server exklusiv (max. einer pro Kunde). */
  responsibilities?: ContactResponsibility[];
  isActive?: boolean;
}

export function createCustomerCareApi(request: RequestFn) {
  return {
    async getCustomerCare(ident: { customerId?: string; weclappCustomerId?: string; cwCompanyId?: number }) {
      const params = new URLSearchParams();
      if (ident.customerId) params.set('customerId', ident.customerId);
      if (ident.weclappCustomerId) params.set('weclappCustomerId', ident.weclappCustomerId);
      if (ident.cwCompanyId != null) params.set('cwCompanyId', String(ident.cwCompanyId));
      return request<CustomerCareData>(`/customer-care?${params.toString()}`);
    },

    /** Mail-Domains des Kunden — Grundlage für Portal-Anfragen und Ticket-Zuordnung */
    async getCustomerEmailDomains(customerId: string) {
      return request<{ domains: string[] }>(`/customer-care/${encodeURIComponent(customerId)}/email-domains`);
    },

    /** Nur Admins. Normalisiert serverseitig („max@Curato.DE" → „curato.de"). */
    async addCustomerEmailDomain(customerId: string, domain: string) {
      return request<{ domains: string[] }>(`/customer-care/${encodeURIComponent(customerId)}/email-domains`, {
        method: 'POST',
        body: JSON.stringify({ domain }),
      });
    },

    async removeCustomerEmailDomain(customerId: string, domain: string) {
      return request<{ domains: string[] }>(
        `/customer-care/${encodeURIComponent(customerId)}/email-domains/${encodeURIComponent(domain)}`,
        { method: 'DELETE' }
      );
    },

    /** Nur der Öffnungsstatus — für Badges an Cockpit, Chat, Rückrufliste. */
    async getCustomerOpeningStatus(ident: { customerId?: string; weclappCustomerId?: string; cwCompanyId?: number }) {
      const params = new URLSearchParams();
      if (ident.customerId) params.set('customerId', ident.customerId);
      if (ident.weclappCustomerId) params.set('weclappCustomerId', ident.weclappCustomerId);
      if (ident.cwCompanyId != null) params.set('cwCompanyId', String(ident.cwCompanyId));
      return request<{
        customerId: string;
        customerName: string;
        openingHours: OpeningHours | null;
        status: OpeningStatus;
      }>(
        `/customer-care/opening-status?${params.toString()}`
      );
    },

    /** KIAN sucht die Öffnungszeiten im Web — nur ein Vorschlag, gespeichert wird nichts. */
    async researchCustomerOpeningHours(customerId: string) {
      return request<OpeningHoursResearchResult>(
        `/customer-care/${encodeURIComponent(customerId)}/opening-hours/research`,
        { method: 'POST', body: JSON.stringify({}) }
      );
    },

    /** null entfernt die Öffnungszeiten — dann zeigt der Support keinen Status mehr. */
    async updateCustomerOpeningHours(customerId: string, openingHours: OpeningHours | null) {
      return request<{ success: true; openingHours: OpeningHours | null; openingStatus: OpeningStatus }>(
        `/customer-care/${encodeURIComponent(customerId)}/opening-hours`,
        { method: 'PUT', body: JSON.stringify({ openingHours }) }
      );
    },

    async updateCustomerServiceNotes(customerId: string, serviceNotes: string) {
      return request<{ success: true }>(`/customer-care/${encodeURIComponent(customerId)}/notes`, {
        method: 'PATCH',
        body: JSON.stringify({ serviceNotes }),
      });
    },

    /** null entfernt das Kontingent — dann taucht es im Rückblick nirgends auf. */
    async updateCustomerContingent(customerId: string, includedHoursPerMonth: number | null) {
      return request<{ success: true; includedHoursPerMonth: number | null }>(
        `/customer-care/${encodeURIComponent(customerId)}/contingent`,
        { method: 'PATCH', body: JSON.stringify({ includedHoursPerMonth }) }
      );
    },

    /**
     * Vertragsmodell des Kunden. Steuert nicht die Abrechnung, sondern den Hinweis, den
     * der Techniker im Cockpit sieht — `UNKNOWN` blendet ihn aus.
     */
    async updateCustomerContractModel(customerId: string, contractModel: CustomerContractModel) {
      return request<{ success: true; contractModel: CustomerContractModel }>(
        `/customer-care/${encodeURIComponent(customerId)}/contract-model`,
        { method: 'PATCH', body: JSON.stringify({ contractModel }) }
      );
    },

    /**
     * Abgleich mit dem Leistungsspektrum — immer vollständig, auch die nie bewerteten
     * Leistungen. In denen steckt die Lücke, um die es geht.
     */
    async getCustomerPortfolio(customerId: string) {
      return request<CustomerPortfolioMatch>(
        `/customer-care/${encodeURIComponent(customerId)}/portfolio`
      );
    },

    /** `status: null` nimmt die Bewertung zurück auf „noch nie besprochen". */
    async setCustomerOffering(
      customerId: string,
      offeringKey: string,
      data: { status: CustomerOfferingStatus | null; note?: string | null; since?: string | null }
    ) {
      return request<{ success: true; summary: CustomerPortfolioMatch['summary'] }>(
        `/customer-care/${encodeURIComponent(customerId)}/portfolio/${encodeURIComponent(offeringKey)}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
    },

    async getFairUseCatalog() {
      return request<FairUseCatalog>('/customer-care/fair-use-catalog');
    },

    /**
     * Schreibt die VOLLE Fassung fest, nicht die Abweichung — eine Vereinbarung darf
     * sich nicht ändern, weil später jemand den Hausstandard anfasst. `null` löscht
     * sie, dann gilt wieder der Hausstandard.
     */
    async updateCustomerFairUse(customerId: string, fairUse: FairUsePolicy | null) {
      return request<{ success: true; fairUse: FairUsePolicy; fairUseIsOwn: boolean }>(
        `/customer-care/${encodeURIComponent(customerId)}/fair-use`,
        { method: 'PATCH', body: JSON.stringify({ fairUse }) }
      );
    },

    async createCareContact(customerId: string, data: Omit<CareContactInput, 'isActive'> & { name: string }) {
      return request<{ contact: CareContact }>(`/customer-care/${encodeURIComponent(customerId)}/contacts`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * CRM-Abweichung entscheiden: Werte übernehmen oder Vermerk verwerfen.
     * In beiden Fällen verschwindet der Hinweis am Kontakt.
     */
    async resolveCareContactDivergence(
      contactId: string,
      action: 'adopt' | 'dismiss',
      fields?: (keyof CrmDivergence)[],
    ) {
      return request<{ contact: CareContact }>(
        `/customer-care/contacts/${contactId}/crm-divergence`,
        { method: 'POST', body: JSON.stringify({ action, ...(fields ? { fields } : {}) }) },
      );
    },

    async updateCareContact(contactId: string, data: CareContactInput) {
      return request<{ contact: CareContact }>(`/customer-care/contacts/${encodeURIComponent(contactId)}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    /**
     * Löschen setzt einen Löschvermerk: weg aus Liste und Zuordnung, der Sync legt den
     * Kontakt nicht wieder an. Portal-Zugang und laufende Sitzungen enden dabei.
     */
    async deleteCareContact(contactId: string) {
      return request<{ success: true }>(`/customer-care/contacts/${encodeURIComponent(contactId)}`, {
        method: 'DELETE',
      });
    },

    /** Löschen rückgängig machen. `isActive` = Stand vor dem Löschen; der Portal-Zugang bleibt entzogen. */
    async restoreCareContact(contactId: string, isActive: boolean) {
      return request<{ contact: CareContact }>(
        `/customer-care/contacts/${encodeURIComponent(contactId)}/restore`,
        { method: 'POST', body: JSON.stringify({ isActive }) },
      );
    },

    /**
     * Mitarbeiter-Ansicht: liefert die einmalige URL, unter der das Kundenportal in der
     * Sicht dieses Kontakts aufgeht (nur lesen, feste Laufzeit). Sofort öffnen — die URL
     * verfällt nach wenigen Minuten.
     *
     * `assume`: mit angenommener Auswahl öffnen („Vorschau mit dieser Auswahl" beim
     * Freischalten) — Rolle und Bereiche statt der echten Rechte des Kontakts.
     */
    async startCustomerPortalPreview(contactId: string, assume?: PortalAreaValue) {
      return request<{ url: string; handoffExpiresAt: string; sessionMinutes: number }>(
        `/customer-care/contacts/${encodeURIComponent(contactId)}/portal-preview`,
        assume ? { method: 'POST', body: JSON.stringify({ assume }) } : { method: 'POST' },
      );
    },
  };
}
